import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input } from "reactstrap";
import { getReservations, fetchBranches, addReservationToWaitlist } from "../../utils/apiCalls";
import { exportToCsv } from "../../utils/exportCsv";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const statusBadge = (s) => {
  const map = {
    pending: { color: C.orange, bg: C.orangeLight },
    confirmed: { color: C.blue, bg: C.blueLight },
    seated: { color: C.purple, bg: C.purpleLight },
    completed: { color: C.green, bg: C.greenLight },
    cancelled: { color: C.textLight, bg: C.borderLight },
    no_show: { color: C.coral, bg: C.coralLight },
  };
  return map[s] || { color: C.textMed, bg: C.borderLight };
};

const today = () => new Date().toISOString().split("T")[0];

const ReservationList = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      setBranches(list);
    };
    load();
  }, [selectedMerchant]);

  const fetchData = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") { setReservations([]); return; }
    setIsLoading(true);
    const params = { MerchantId: selectedMerchant, page: currentPage, limit: 20 };
    if (selectedBranch) params.branchId = selectedBranch;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (filterStatus) params.status = filterStatus;
    if (search.trim()) params.search = search.trim();
    const data = await getReservations(params);
    setReservations(data?.rows || []);
    setTotalPages(data?.totalPages || 0);
    setTotal(data?.total || 0);
    setIsLoading(false);
  }, [selectedMerchant, selectedBranch, dateFrom, dateTo, filterStatus, search, currentPage]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setCurrentPage(1); }, [selectedBranch, dateFrom, dateTo, filterStatus, search]);

  const setToday = () => { setDateFrom(today()); setDateTo(today()); };
  const setThisWeek = () => {
    const now = new Date();
    const start = new Date(now); start.setDate(now.getDate() - now.getDay());
    const end = new Date(start); end.setDate(start.getDate() + 6);
    setDateFrom(start.toISOString().split("T")[0]);
    setDateTo(end.toISOString().split("T")[0]);
  };
  const clearDates = () => { setDateFrom(""); setDateTo(""); };

  const handleExport = () => {
    exportToCsv(`reservations_${dateFrom || "all"}_${dateTo || "all"}.csv`, reservations, [
      { key: "id", label: "ID" }, { key: "customerName", label: "Customer" }, { key: "customerPhone", label: "Phone" },
      { key: "guestCount", label: "Guests" }, { key: "reservationDate", label: "Date" }, { key: "startTime", label: "Time" },
      { key: (r) => r.Branch?.name || "", label: "Branch" }, { key: (r) => r.Table?.name || "Unassigned", label: "Table" },
      { key: "status", label: "Status" }, { key: "source", label: "Source" },
    ]);
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Reservations" parent="Reservations" />
      <Container fluid className="rpt-wrap">
        {/* Filters Row 1 */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          <Input type="select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} style={{ maxWidth: 200, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
          <Input type="select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ maxWidth: 160, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Statuses</option>
            {["pending", "confirmed", "seated", "completed", "cancelled", "no_show"].map((s) => <option key={s} value={s}>{s}</option>)}
          </Input>
          <Input type="text" placeholder="Search name or phone..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 220, borderRadius: 8, border: `1px solid ${C.border}` }} />
          <div style={{ flex: 1 }} />
          {reservations.length > 0 && (
            <>
              <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={handleExport}><i className="fa fa-download" /> CSV</Button>
              <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={() => window.print()}><i className="fa fa-print" /></Button>
            </>
          )}
          <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, padding: "8px 16px" }} onClick={() => navigate("/reservations/add")}>
            + New Reservation
          </Button>
        </div>

        {/* Filters Row 2: Date range */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ maxWidth: 160, borderRadius: 8, border: `1px solid ${C.border}` }} />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ maxWidth: 160, borderRadius: 8, border: `1px solid ${C.border}` }} />
          <Button size="sm" style={{ background: dateFrom === today() && dateTo === today() ? C.coral : C.white, color: dateFrom === today() && dateTo === today() ? "#fff" : C.textMed, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={setToday}>Today</Button>
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={setThisWeek}>This Week</Button>
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={clearDates}>All Dates</Button>
        </div>

        {/* Table */}
        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? <Loader /> : (!selectedMerchant || selectedMerchant === "all") ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>Select a merchant</p>
          ) : reservations.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>No reservations found</p>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      {["ID", "Customer", "Guests", "Date", "Time", "Branch", "Table", "Status", "Source", ""].map((h) => (
                        <th key={h} style={tbl.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.map((r) => {
                      const sb = statusBadge(r.status);
                      return (
                        <tr key={r.id} className="rpt-row">
                          <td style={tbl.td}>{r.id}</td>
                          <td style={tbl.td}>
                            <div style={{ fontWeight: 600, color: C.text }}>{r.customerName}</div>
                            {r.customerPhone && <div style={{ fontSize: 11, color: C.textLight }}>{r.customerPhone}</div>}
                          </td>
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{r.guestCount}</td>
                          <td style={tbl.td}>{r.reservationDate}</td>
                          <td style={{ ...tbl.td, fontWeight: 600 }}>{r.startTime}</td>
                          <td style={tbl.td}>{r.Branch?.name || "—"}</td>
                          <td style={tbl.td}>{r.Table ? <Badge color={C.blue} bg={C.blueLight}>{r.Table.name}</Badge> : <span style={{ color: C.textLight }}>Unassigned</span>}</td>
                          <td style={tbl.td}><Badge color={sb.color} bg={sb.bg}>{r.status}</Badge></td>
                          <td style={tbl.td}><Badge color={C.textMed} bg={C.borderLight}>{r.source}</Badge></td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
                              {r.status === "confirmed" && (
                                <span
                                  title="Add this guest to the waitlist"
                                  style={{ cursor: "pointer", color: C.coral, fontWeight: 700, fontSize: 11 }}
                                  onClick={async () => {
                                    if (!window.confirm(`Add ${r.customerName} (party of ${r.guestCount}) to the waitlist?`)) return;
                                    const result = await addReservationToWaitlist(r.id);
                                    if (result) navigate(`/table-management/edit-waitlist/${result.id}`);
                                  }}
                                >
                                  + Waitlist
                                </span>
                              )}
                              <span style={{ cursor: "pointer", color: C.blue, fontWeight: 600, fontSize: 12 }} onClick={() => navigate(`/reservations/edit/${r.id}`)}>
                                <i className="fa fa-pencil" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: `1px solid ${C.borderLight}` }}>
                  <span style={{ fontSize: 12, color: C.textLight }}>Showing {(currentPage - 1) * 20 + 1}–{Math.min(currentPage * 20, total)} of {total}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</Button>
                    <span style={{ padding: "6px 12px", fontSize: 12, color: C.textMed }}>{currentPage} / {totalPages}</span>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </RCard>
      </Container>
    </Fragment>
  );
};

export default ReservationList;
